import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { getMopOutput } from "../../api/client";
import type { MopOutputDetail } from "../../api/types";
import { DetailPageShell } from "../../lib/DetailPageShell";
import { formatMopDate, MopStatus, operationLabel } from "./mopView";
import "./mop.css";

export function MopDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [data, setData] = useState<MopOutputDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    setData(null); setError(null);
    getMopOutput(id, controller.signal).then((result) => {
      if (!controller.signal.aborted) setData(result);
    }).catch((e) => {
      if (!controller.signal.aborted) setError(e instanceof Error ? e.message : "Impossibile caricare la richiesta MOP");
    });
    return () => controller.abort();
  }, [id, refresh]);

  function download() {
    if (!data) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(data.payload, null, 2)], { type: "application/json" }));
    const link = document.createElement("a"); link.href = url; link.download = `mop-${data.request_id}.json`;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  return <DetailPageShell backTo={`/mop${location.search}`} backLabel="Uscite MOP"
    title={data ? `MOP · ${operationLabel(data.operazione)}` : "MOP"} titleId={data?.request_id}
    badges={data && <MopStatus state={data.delivery_state} />}
    meta={data && <span>{data.descrizione_edificio || data.codice_edificio} · {data.codice_impianto}</span>}
    extraActions={<><button className="btn btn-outline btn-sm" onClick={() => setRefresh((v) => v + 1)}>Aggiorna</button>
      <button className="btn btn-primary btn-sm" disabled={!data} onClick={download}>Scarica JSON</button></>}>
    <div className="mop-page mop-detail-content">
    {error ? <div className="card mop-error" role="alert">{error}</div> : !data ? <div className="loading-center"><div className="spinner" /><span>Caricamento MOP…</span></div> : <>
      <section className="card"><h2>Richiesta</h2><p className="mop-text">{data.motivo}</p>
        <dl className="mop-fields"><div><dt>Edificio</dt><dd>{data.descrizione_edificio || "—"} · {data.codice_edificio}</dd></div>
          <div><dt>Impianto</dt><dd>{data.descrizione_impianto || "—"} · {data.codice_impianto}</dd></div>
          <div><dt>Cliente</dt><dd>{data.payload.cliente.ragioneSociale || data.payload.cliente.codice || "—"}</dd></div>
          <div><dt>Priorità</dt><dd>{data.payload.priorita || "—"}</dd></div>
        </dl>
      </section>
      <section className="card"><h2>Copertura MOP</h2><p className="text-muted">MOP e attività che coprono questa richiesta sull’impianto identificato.</p>
        {data.payload.mopRiferimenti.map((ref) => <article className="mop-contract" key={ref.codiceMop}>
          <h3>{ref.codiceMop}</h3><dl className="mop-fields"><div><dt>Contratto</dt><dd>{ref.idContratto ?? "—"}</dd></div>
            <div><dt>Servizio</dt><dd>{ref.servizioDescr || ref.servizio || "—"}</dd></div><div><dt>Data MOP</dt><dd>{formatMopDate(ref.dataMop)}</dd></div><div><dt>Stato MOP</dt><dd>{ref.stato ?? "—"}</dd></div></dl>
          <ul className="mop-activities">{ref.attivita.map((activity) => <li key={activity.codiceManutenzione}>
            <strong>{activity.obiettivo || "Attività programmata"}</strong><div className="text-sm text-muted">{[activity.tipoComponente, activity.componente, activity.marca, activity.modello].filter(Boolean).join(" · ")}</div>
            <div className="text-xs text-muted">Attività {activity.codiceManutenzione}{activity.codiceUnivoco ? ` · Componente ${activity.codiceUnivoco}` : ""}</div>
          </li>)}</ul>
        </article>)}
      </section>
      <section className="card"><h2>Storico invio</h2><dl className="mop-fields">
        <div><dt>Uscita creata</dt><dd>{formatMopDate(data.created_at)}</dd></div><div><dt>Inviata</dt><dd>{formatMopDate(data.sent_at)}</dd></div>
        <div><dt>Tentativi</dt><dd>{data.attempts}</dd></div><div><dt>Riferimento</dt><dd>{data.request_id}</dd></div>
      </dl>
        {data.delivery_state === "invalidated" && <p>Questa uscita richiede una nuova verifica della pratica prima dell’invio.</p>}
        {data.last_error && <p className="mop-error" role="alert">{data.last_error}</p>}
        <Link className="btn btn-outline" to={`/mop/requests/${encodeURIComponent(data.request_id)}`}>Apri pratica e storico</Link>
      </section>
      <section className="card"><h2>Email originale</h2><dl className="mop-fields"><div><dt>Oggetto</dt><dd>{data.subject || "—"}</dd></div><div><dt>Mittente</dt><dd>{data.sender || "—"}</dd></div>
        <div><dt>Ricevuta</dt><dd>{formatMopDate(data.received_at)}</dd></div><div><dt>Riferimento richiesta</dt><dd>{data.payload.richiesta.numeroRiferimento || "—"}</dd></div></dl>
        <details><summary>Leggi il testo della richiesta</summary><pre className="mop-text">{data.payload.richiesta.testo}</pre></details>
        {data.payload.richiesta.allegati.length > 0 && <p className="text-sm">Allegati: {data.payload.richiesta.allegati.map((a) => a.nome).join(", ")}</p>}
      </section>
    </>}
    </div>
  </DetailPageShell>;
}
